/**
 * Bootstrap, game loop, wiring.
 *
 * The loop is a fixed-timestep accumulator: simulation advances in exact
 * TICK_MS steps, rendering runs on requestAnimationFrame and only reads state.
 * Render code must never mutate the game.
 *
 * The canvas redraws every frame. Text updates run at UI_FPS - numbers do not
 * need 60, and touching text nodes 60 times a second is wasted budget.
 */

import Decimal from 'break_infinity.js';

import {
  AUTOSAVE_INTERVAL_SECONDS,
  LAMP_CRITICAL_HEAT,
  LAMP_WARN_HEAT,
  LEVER_UNLOCK_COUNT,
  MELTDOWN_BLACKOUT_MS,
  SHAKE_HEAT,
  UI_FPS,
} from './data/balance.ts';
import { GENERATORS } from './data/generators.ts';

import { advance, createAccumulator } from './sim/loop.ts';
import { createTickEvents, tick } from './sim/tick.ts';
import {
  bulkCount,
  getModifiers,
  greyMatterGain,
  tps,
  voidShare,
  type Modifiers,
} from './sim/formulas.ts';
import { buyGenerator, buySkill, setAutoBuy, setClock, vent } from './sim/actions.ts';
import { collectOffline, computeOffline } from './sim/offline.ts';
import type { GameState } from './sim/state.ts';

import { deserialize, loadGame, saveGame, serialize } from './save/save.ts';

import { byId, setText } from './ui/dom.ts';
import { formatDuration, formatNumber, formatRate } from './ui/format.ts';
import { initToasts, toast } from './ui/toast.ts';
import { createModal } from './ui/modal.ts';
import { createGeneratorPanel, type BulkMode } from './ui/panels/generators.ts';
import { createClockPanel } from './ui/panels/clock.ts';
import { createMeltdownPanel } from './ui/panels/meltdown.ts';
import { createSkillPanel } from './ui/panels/skills.ts';
import { createStatsPanel } from './ui/panels/stats.ts';
import { buildManual } from './ui/panels/manual.ts';
import { currentDirective } from './sim/directives.ts';

import { createVessel, drawVessel, pulseVessel, resetVessel } from './render/vessel.ts';
import { sparkRateFor } from './render/neural.ts';

const UI_INTERVAL_MS = 1000 / UI_FPS;

// ---------------------------------------------------------------------------
// Boot
// ---------------------------------------------------------------------------

const load = loadGame(Date.now());

/**
 * The live game. Exported as a read-only handle for tests and the dev console;
 * nothing in the render path may write to it.
 */
export const state: GameState = load.state;

/** Live loop diagnostics, so tests measure the same counter the loop keeps. */
export const diagnostics = { ticksTotal: 0, tickRate: 0 };

initToasts(byId('toasts'));

if (load.kind === 'broken') {
  toast(
    "Save couldn't be read. Started a new run — the old data is still in browser storage.",
  );
}

let mods: Modifiers = getModifiers(state);

// ---------------------------------------------------------------------------
// Panels
// ---------------------------------------------------------------------------

let bulk: BulkMode = 1;

const generatorsPanel = createGeneratorPanel(byId('generators'), {
  onBuy(index) {
    if (buyGenerator(state, index, bulkCount(state, index, bulk))) {
      // A purchase should do something you can see, not only change a number.
      pulseVessel(vessel, 14);
      checkLeverUnlock();
      renderUI();
    }
  },
  onAutoBuyChange(id, enabled) {
    setAutoBuy(state, id, enabled);
  },
});

const clockPanel = createClockPanel((value) => {
  setClock(state, value, mods);
  renderUI();
});

const meltdownPanel = createMeltdownPanel({
  onVent() {
    if (vent(state, mods)) renderUI();
    else toast('Vent is still recharging.');
  },
  onMeltdown() {
    if (greyMatterGain(state, mods).lte(0)) {
      toast('Not yet worth an idea. Keep running.');
      return;
    }
    openConfirm();
  },
  onAutoMeltChange(enabled, threshold) {
    state.settings.autoMeltdownEnabled = enabled;
    if (Number.isFinite(threshold)) {
      state.settings.autoMeltdownThreshold = Math.min(99, Math.max(50, threshold));
    }
  },
});

const skillPanel = createSkillPanel((id) => {
  if (buySkill(state, id)) {
    mods = getModifiers(state);
    setClock(state, state.clock, mods);
    skillPanel.update(state);
    renderUI();
  }
});

const statsPanel = createStatsPanel();

const vesselCanvas = byId<HTMLCanvasElement>('vessel');
const vessel = createVessel(vesselCanvas, (art) => {
  // Match the panel to the artwork so cover-fit crops as little as possible.
  vesselCanvas.style.aspectRatio = String(art.aspect);
  vesselCanvas.style.height = 'auto';
});

// --- Header and chassis -----------------------------------------------------

const chassis = byId('chassis');
const lamp = byId('lamp');
const runtimeLabel = byId('runtime');
const thoughtsLabel = byId('thoughts');
const tpsLabel = byId('tps');
const blackout = byId('blackout');
const directive = byId('directive');
const directiveLabel = byId('directive-label');
const directiveBody = byId('directive-body');

// --- Bulk buy selector ------------------------------------------------------

const buybar = byId('buybar');
buybar.addEventListener('click', (event) => {
  const button = (event.target as HTMLElement).closest<HTMLButtonElement>('[data-bulk]');
  if (button === null) return;
  const raw = button.dataset.bulk;
  bulk = raw === 'max' ? 'max' : Number(raw);
  for (const b of buybar.querySelectorAll('[data-bulk]')) {
    b.classList.toggle('is-active', b === button);
  }
  renderUI();
});

// --- Modals -----------------------------------------------------------------

const skillsModal = createModal(byId('skills-modal'), () => skillPanel.update(state));
const statsModal = createModal(byId('stats-modal'), () => statsPanel.update(state));
const settingsModal = createModal(byId('settings-modal'), syncSettings);
const manualModal = createModal(byId('manual-modal'), buildManual);
const explainModal = createModal(byId('explain-modal'));
const confirmModal = createModal(byId('confirm-modal'));
const offlineModal = createModal(byId('offline-modal'));

byId('open-skills').addEventListener('click', () => skillsModal.open());
byId('open-stats').addEventListener('click', () => statsModal.open());
byId('open-settings').addEventListener('click', () => settingsModal.open());
byId('skills-close').addEventListener('click', () => skillsModal.close());
byId('stats-close').addEventListener('click', () => statsModal.close());
byId('settings-close').addEventListener('click', () => settingsModal.close());
byId('confirm-cancel').addEventListener('click', () => confirmModal.close());
byId('open-manual').addEventListener('click', () => manualModal.open());
byId('manual-close').addEventListener('click', () => manualModal.close());
byId('explain-ok').addEventListener('click', () => explainModal.close());
byId('explain-manual').addEventListener('click', () => {
  explainModal.close();
  manualModal.open();
});

function openConfirm(): void {
  const format = state.settings.numberFormat;
  const gain = greyMatterGain(state, mods);
  setText(
    byId('confirm-body'),
    `Forcing the idea melts the brain, and cannot be undone. ` +
      `It destroys ${formatNumber(state.thoughts, format)} sparks and every generator ` +
      `you have installed. You keep ${formatNumber(gain, format)} idea` +
      `${gain.eq(1) ? '' : 's'}, and every skill you have bought.`,
  );
  confirmModal.open();
}

byId('confirm-ok').addEventListener('click', () => {
  confirmModal.close();
  // Force the meltdown through the simulation so there is exactly one code
  // path that melts the rig: push heat to the top and let the next tick fire.
  state.heat = 100;
});

// ---------------------------------------------------------------------------
// Settings
// ---------------------------------------------------------------------------

const formatSelect = byId<HTMLSelectElement>('setting-format');
const motionSelect = byId<HTMLSelectElement>('setting-motion');
const saveBox = byId<HTMLTextAreaElement>('save-box');

formatSelect.addEventListener('change', () => {
  state.settings.numberFormat = formatSelect.value === 'scientific' ? 'scientific' : 'standard';
  renderUI();
});

motionSelect.addEventListener('change', () => {
  state.settings.reducedMotion =
    motionSelect.value === 'null' ? null : motionSelect.value === 'true';
  applyMotionPreference();
});

byId('save-export').addEventListener('click', () => {
  saveBox.value = btoa(serialize(state));
  saveBox.select();
  toast('Save exported. Copy the text below.');
});

byId('save-import').addEventListener('click', () => {
  const raw = saveBox.value.trim();
  if (raw === '') {
    toast('Paste a save string first.');
    return;
  }
  try {
    const next = deserialize(atob(raw), Date.now());
    Object.assign(state, next);
    mods = getModifiers(state);
    saveGame(state, Date.now());
    settingsModal.close();
    resetVessel(vessel);
    renderUI();
    toast('Save imported.');
  } catch {
    toast('That save string could not be read. Nothing was changed.');
  }
});

function syncSettings(): void {
  formatSelect.value = state.settings.numberFormat;
  motionSelect.value =
    state.settings.reducedMotion === null ? 'null' : String(state.settings.reducedMotion);
  saveBox.value = '';
}

const motionQuery = globalThis.matchMedia?.('(prefers-reduced-motion: reduce)');

function reducedMotion(): boolean {
  return state.settings.reducedMotion ?? motionQuery?.matches ?? false;
}

function applyMotionPreference(): void {
  document.body.classList.toggle('reduced-motion', reducedMotion());
}

applyMotionPreference();

// ---------------------------------------------------------------------------
// Offline progress
// ---------------------------------------------------------------------------

{
  const report = computeOffline(state, Date.now(), mods);
  if (report.worthShowing) {
    setText(
      byId('offline-body'),
      `The rig ran unattended for ${formatDuration(report.elapsedSeconds)}. ` +
        `It banked ${formatNumber(report.gain, state.settings.numberFormat)} thoughts at reduced output. ` +
        `Core temperature was not simulated.`,
    );
    byId('offline-collect').addEventListener(
      'click',
      () => {
        collectOffline(state, report);
        offlineModal.close();
        renderUI();
      },
      { once: true },
    );
    offlineModal.open();
  } else {
    // Short gaps are collected silently.
    collectOffline(state, report);
  }
}

// ---------------------------------------------------------------------------
// Loop
// ---------------------------------------------------------------------------

const accumulator = createAccumulator();
const events = createTickEvents();

let lastFrameMs = performance.now();
let lastUiMs = 0;
let sinceSaveMs = 0;
let ticksInWindow = 0;
let windowStartMs = lastFrameMs;
let meltdownUntilMs = 0;

function frame(nowMs: number): void {
  requestAnimationFrame(frame);

  const elapsedMs = nowMs - lastFrameMs;
  lastFrameMs = nowMs;

  events.meltdown = null;
  events.revealed.length = 0;

  const steps = advance(accumulator, elapsedMs, (dt) => tick(state, dt, mods, events));

  diagnostics.ticksTotal += steps;
  ticksInWindow += steps;
  if (nowMs - windowStartMs >= 1000) {
    diagnostics.tickRate = (ticksInWindow * 1000) / (nowMs - windowStartMs);
    ticksInWindow = 0;
    windowStartMs = nowMs;
  }

  if (events.meltdown !== null) handleMeltdown(events.meltdown, nowMs);
  if (events.revealed.length > 0) {
    checkLeverUnlock();
    if (events.revealed.includes('void')) {
      state.unlocks.voidExplained = false;
      toast('Something else is awake in there.');
    }
  }

  sinceSaveMs += elapsedMs;
  if (sinceSaveMs >= AUTOSAVE_INTERVAL_SECONDS * 1000) {
    sinceSaveMs = 0;
    saveGame(state, Date.now());
  }

  renderCanvas(nowMs, Math.min(elapsedMs, 100) / 1000);

  if (nowMs - lastUiMs >= UI_INTERVAL_MS) {
    lastUiMs = nowMs;
    renderUI();
  }
}

// ---------------------------------------------------------------------------
// Meltdown sequence
// ---------------------------------------------------------------------------

function handleMeltdown(result: { gain: Decimal; auto: boolean }, nowMs: number): void {
  meltdownUntilMs = nowMs + MELTDOWN_BLACKOUT_MS;
  vessel.drain = 1;

  blackout.classList.add('is-active');
  globalThis.setTimeout(() => {
    blackout.classList.remove('is-active');
    resetVessel(vessel);
  }, MELTDOWN_BLACKOUT_MS);

  const amount = formatNumber(result.gain, state.settings.numberFormat);

  // The first one needs explaining. After that a toast is enough.
  if (state.stats.meltdowns === 1) {
    byId('explain-body').replaceChildren(
      paragraph(
        `The core reached 100 degrees and the brain melted. That is not a mistake — ` +
          `it is how the game pays out. Everything it had been thinking came out as ` +
          `one idea.`,
      ),
      paragraph(
        `The run is gone: every spark and every generator. In exchange you banked ` +
          `${amount} idea${result.gain.eq(1) ? '' : 's'}, which are permanent and survive ` +
          `every future meltdown.`,
      ),
      paragraph(
        `Spend them on Memories. Those are the only thing that carries ` +
          `forward, and they are what makes the next run faster than the one you just lost.`,
      ),
    );
    explainModal.open();
    return;
  }

  toast(
    result.auto
      ? `Failsafe tripped. The brain produced ${amount} idea${result.gain.eq(1) ? '' : 's'}.`
      : `Meltdown complete. The brain produced ${amount} idea${result.gain.eq(1) ? '' : 's'}.`,
  );

  saveGame(state, Date.now());
}

// ---------------------------------------------------------------------------
// Progression gates
// ---------------------------------------------------------------------------

function paragraph(text: string): HTMLParagraphElement {
  const node = document.createElement('p');
  node.style.margin = '0 0 10px';
  node.textContent = text;
  return node;
}

function checkLeverUnlock(): void {
  if (state.unlocks.lever === true) return;
  if ((state.generators[0] ?? 0) < LEVER_UNLOCK_COUNT) return;
  state.unlocks.lever = true;
  toast('Manufacturer spec is 100%. The rig will go further.');
}

// ---------------------------------------------------------------------------
// Render - reads state, never writes it
// ---------------------------------------------------------------------------

function renderCanvas(nowMs: number, dt: number): void {
  let totalGenerators = 0;
  for (const owned of state.generators) totalGenerators += owned;

  drawVessel(vessel, {
    heat: state.heat,
    peakHeat: state.peakHeatThisRun,
    clock: state.clock,
    clockExp: mods.clockExp,
    sparkRate: sparkRateFor(tps(state, mods).toNumber()),
    totalGenerators,
    voidShare: voidShare(state, mods),
    timeSec: nowMs / 1000,
    dt,
    reducedMotion: reducedMotion(),
  });

  // Shake at the redline, amplitude (heat - 95) / 5 * 3px.
  const shaking = state.heat >= SHAKE_HEAT && !reducedMotion() && nowMs > meltdownUntilMs;
  if (shaking) {
    const amplitude = ((state.heat - SHAKE_HEAT) / 5) * 3;
    chassis.style.setProperty('--shake', `${amplitude.toFixed(2)}px`);
  }
  chassis.classList.toggle('is-shaking', shaking);
}

function renderUI(): void {
  const format = state.settings.numberFormat;

  setText(thoughtsLabel, formatNumber(state.thoughts, format));
  setText(tpsLabel, formatRate(tps(state, mods), format));
  setText(runtimeLabel, formatDuration(state.runTimeSec));

  generatorsPanel.update(state, mods, bulk);
  clockPanel.update(state, mods);
  meltdownPanel.update(state, mods);
  if (skillsModal.isOpen) skillPanel.update(state);
  if (statsModal.isOpen) statsPanel.update(state);

  const order = currentDirective(state, mods);
  setText(directiveLabel, order.label);
  setText(directiveBody, order.body);
  directive.classList.toggle('is-teaching', order.teaching);
  directive.classList.toggle('is-critical', order.label === 'Critical');

  lamp.classList.toggle('is-warn', state.heat >= LAMP_WARN_HEAT && state.heat < LAMP_CRITICAL_HEAT);
  lamp.classList.toggle('is-critical', state.heat >= LAMP_CRITICAL_HEAT);
}

// ---------------------------------------------------------------------------
// Saving
// ---------------------------------------------------------------------------

document.addEventListener('visibilitychange', () => {
  if (document.visibilityState === 'hidden') saveGame(state, Date.now());
});

window.addEventListener('beforeunload', () => {
  saveGame(state, Date.now());
});

// Keyboard shortcuts for the two controls a player uses constantly.
document.addEventListener('keydown', (event) => {
  if (event.target instanceof HTMLInputElement || event.target instanceof HTMLSelectElement) return;
  if (event.key === 'v' || event.key === 'V') {
    if (vent(state, mods)) renderUI();
  }
});

// ---------------------------------------------------------------------------
// Go
// ---------------------------------------------------------------------------

// A save from before the reveal system existed still needs its owned tiers
// visible on the first frame.
for (let i = 0; i < GENERATORS.length; i++) {
  const def = GENERATORS[i];
  if (def !== undefined && (state.generators[i] ?? 0) > 0) {
    state.unlocks[`gen:${def.id}`] = true;
  }
}
checkLeverUnlock();

renderUI();
requestAnimationFrame(frame);
